import React, { useState, useEffect } from "react";
import config from "../../config/config";

const ContactFormData = () => {
  const [submissions, setSubmissions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selectedSubmission, setSelectedSubmission] = useState(null);
  const [showDetailsModal, setShowDetailsModal] = useState(false);
  const [filters, setFilters] = useState({
    status: "all",
    dateFrom: "",
    dateTo: "",
    searchTerm: "",
  });

  // Fetch submissions on component mount
  useEffect(() => {
    fetchSubmissions();
  }, []);

  // Fetch contact form submissions
  const fetchSubmissions = async () => {
    try {
      setLoading(true);
      const queryParams = new URLSearchParams({
        status: filters.status,
        dateFrom: filters.dateFrom,
        dateTo: filters.dateTo,
        search: filters.searchTerm,
      });

      const response = await fetch(
        config.getAdminApiUrl(`get_contact_form_data.php?${queryParams}`),
        {
          headers: {
            "X-API-Key": "ncg_admin_2024",
          },
        }
      );
      const data = await response.json();

      if (data.success) {
        setSubmissions(data.submissions);
      }
    } catch (error) {
      console.error("Error fetching submissions:", error);
    } finally {
      setLoading(false);
    }
  };

  // Handle filter changes
  const handleFilterChange = (e) => {
    const { name, value } = e.target;
    setFilters({ ...filters, [name]: value });
  };

  // Apply filters
  const applyFilters = () => {
    fetchSubmissions();
  };

  // Handle status update
  const handleStatusUpdate = async (submissionId, newStatus) => {
    try {
      setLoading(true);
      const response = await fetch(config.getAdminApiUrl("update_form_status.php"), {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "X-API-Key": "ncg_admin_2024",
        },
        body: JSON.stringify({
          form_type: "contact",
          submission_id: submissionId,
          status: newStatus,
        }),
      });

      const data = await response.json();

      if (data.success) {
        fetchSubmissions();
        alert("Status updated successfully!");
      } else {
        alert("Error updating status: " + data.message);
      }
    } catch (error) {
      console.error("Error updating status:", error);
      alert("Error updating status");
    } finally {
      setLoading(false);
    }
  };

  // Handle submission deletion
  const handleDelete = async (submissionId) => {
    if (!window.confirm("Are you sure you want to delete this submission?"))
      return;

    try {
      setLoading(true);
      const response = await fetch(
        config.getAdminApiUrl("delete_contact_submission.php"),
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            "X-API-Key": "ncg_admin_2024",
          },
          body: JSON.stringify({ id: submissionId }),
        }
      );

      const data = await response.json();

      if (data.success) {
        fetchSubmissions();
        alert("Submission deleted successfully!");
      } else {
        alert("Error deleting submission: " + data.message);
      }
    } catch (error) {
      console.error("Error deleting submission:", error);
      alert("Error deleting submission");
    } finally {
      setLoading(false);
    }
  };

  // Show submission details
  const showDetails = (submission) => {
    setSelectedSubmission(submission);
    setShowDetailsModal(true);
  };

  // Export submissions to CSV
  const exportToCSV = () => {
    if (submissions.length === 0) {
      alert("No data to export");
      return;
    }

    const headers = [
      "Name",
      "Email",
      "Phone",
      "Subject",
      "Message",
      "Status",
      "Submitted At",
    ];
    const csvContent = [
      headers.join(","),
      ...submissions.map((sub) =>
        [
          `"${sub.name}"`,
          `"${sub.email}"`,
          `"${sub.phone || ""}"`,
          `"${sub.subject || ""}"`,
          `"${sub.message.replace(/"/g, '""')}"`,
          `"${sub.status}"`,
          `"${new Date(sub.created_at).toLocaleString()}"`,
        ].join(",")
      ),
    ].join("\n");

    const blob = new Blob([csvContent], { type: "text/csv" });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `contact_submissions_${
      new Date().toISOString().split("T")[0]
    }.csv`;
    a.click();
    window.URL.revokeObjectURL(url);
  };

  return (
    <div className="contact-form-data">
      <div className="manager-header">
        <h2>Contact Form Submissions</h2>
        <div className="header-actions">
          <button className="btn btn-secondary" onClick={exportToCSV}>
            <i className="fas fa-download me-2"></i>
            Export CSV
          </button>
        </div>
      </div>

      {/* Filters */}
      <div className="manager-content">
        <div className="filters-section">
          <div className="filters-grid">
            <div className="filter-group">
              <label>Status</label>
              <select
                name="status"
                value={filters.status}
                onChange={handleFilterChange}
              >
                <option value="all">All Status</option>
                <option value="new">New</option>
                <option value="read">Read</option>
                <option value="replied">Replied</option>
                <option value="closed">Closed</option>
              </select>
            </div>

            <div className="filter-group">
              <label>From Date</label>
              <input
                type="date"
                name="dateFrom"
                value={filters.dateFrom}
                onChange={handleFilterChange}
              />
            </div>

            <div className="filter-group">
              <label>To Date</label>
              <input
                type="date"
                name="dateTo"
                value={filters.dateTo}
                onChange={handleFilterChange}
              />
            </div>

            <div className="filter-group">
              <label>Search</label>
              <input
                type="text"
                name="searchTerm"
                value={filters.searchTerm}
                onChange={handleFilterChange}
                placeholder="Search name, email, or message..."
              />
            </div>

            <div className="filter-group">
              <label>&nbsp;</label>
              <button className="btn btn-primary" onClick={applyFilters}>
                <i className="fas fa-search me-2"></i>
                Apply Filters
              </button>
            </div>
          </div>
        </div>

        {/* Submissions Table */}
        {loading ? (
          <div className="loading-spinner">
            <i className="fas fa-spinner fa-spin"></i> Loading...
          </div>
        ) : (
          <div className="table-container">
            <table className="admin-table">
              <thead>
                <tr>
                  <th>Name</th>
                  <th>Email</th>
                  <th>Phone</th>
                  <th>Subject</th>
                  <th>Status</th>
                  <th>Submitted</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {submissions.length > 0 ? (
                  submissions.map((submission) => (
                    <tr key={submission.id}>
                      <td>{submission.name}</td>
                      <td>
                        <a href={`mailto:${submission.email}`}>
                          {submission.email}
                        </a>
                      </td>
                      <td>{submission.phone || "—"}</td>
                      <td>{submission.subject || "—"}</td>
                      <td>
                        <select
                          value={submission.status}
                          onChange={(e) =>
                            handleStatusUpdate(submission.id, e.target.value)
                          }
                          className={`status-select ${submission.status}`}
                        >
                          <option value="new">New</option>
                          <option value="read">Read</option>
                          <option value="replied">Replied</option>
                          <option value="closed">Closed</option>
                        </select>
                      </td>
                      <td>
                        {new Date(submission.created_at).toLocaleDateString()}
                      </td>
                      <td>
                        <div className="action-buttons">
                          <button
                            className="btn btn-sm btn-secondary"
                            onClick={() => showDetails(submission)}
                            title="View Details"
                          >
                            <i className="fas fa-eye"></i>
                          </button>
                          <button
                            className="btn btn-sm btn-danger"
                            onClick={() => handleDelete(submission.id)}
                            title="Delete"
                          >
                            <i className="fas fa-trash"></i>
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan="7" className="text-center">
                      No submissions found
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        )}

        {/* Statistics */}
        <div className="stats-section">
          <div className="stats-grid">
            <div className="stat-card">
              <div className="stat-number">
                {submissions.filter((s) => s.status === "new").length}
              </div>
              <div className="stat-label">New</div>
            </div>
            <div className="stat-card">
              <div className="stat-number">
                {submissions.filter((s) => s.status === "read").length}
              </div>
              <div className="stat-label">Read</div>
            </div>
            <div className="stat-card">
              <div className="stat-number">
                {submissions.filter((s) => s.status === "replied").length}
              </div>
              <div className="stat-label">Replied</div>
            </div>
            <div className="stat-card">
              <div className="stat-number">
                {submissions.filter((s) => s.status === "closed").length}
              </div>
              <div className="stat-label">Closed</div>
            </div>
            <div className="stat-card">
              <div className="stat-number">{submissions.length}</div>
              <div className="stat-label">Total</div>
            </div>
          </div>
        </div>
      </div>

      {/* Details Modal */}
      {showDetailsModal && selectedSubmission && (
        <div className="modal-overlay">
          <div className="modal-content large">
            <div className="modal-header">
              <h3>Contact Submission Details</h3>
              <button
                className="modal-close"
                onClick={() => {
                  setShowDetailsModal(false);
                  setSelectedSubmission(null);
                }}
              >
                <i className="fas fa-times"></i>
              </button>
            </div>
            <div className="modal-body">
              <div className="submission-details">
                <div className="detail-row">
                  <strong>Name:</strong>
                  <span>{selectedSubmission.name}</span>
                </div>
                <div className="detail-row">
                  <strong>Email:</strong>
                  <span>
                    <a href={`mailto:${selectedSubmission.email}`}>
                      {selectedSubmission.email}
                    </a>
                  </span>
                </div>
                {selectedSubmission.phone && (
                  <div className="detail-row">
                    <strong>Phone:</strong>
                    <span>
                      <a href={`tel:${selectedSubmission.phone}`}>
                        {selectedSubmission.phone}
                      </a>
                    </span>
                  </div>
                )}
                {selectedSubmission.subject && (
                  <div className="detail-row">
                    <strong>Subject:</strong>
                    <span>{selectedSubmission.subject}</span>
                  </div>
                )}
                <div className="detail-row">
                  <strong>Status:</strong>
                  <span className={`status-badge ${selectedSubmission.status}`}>
                    {selectedSubmission.status}
                  </span>
                </div>
                <div className="detail-row">
                  <strong>Submitted:</strong>
                  <span>
                    {new Date(selectedSubmission.created_at).toLocaleString()}
                  </span>
                </div>
                <div className="detail-row full-width">
                  <strong>Message:</strong>
                  <div className="message-content">
                    {selectedSubmission.message}
                  </div>
                </div>
              </div>
            </div>
            <div className="modal-footer">
              <button
                className="btn btn-secondary"
                onClick={() => {
                  setShowDetailsModal(false);
                  setSelectedSubmission(null);
                }}
              >
                Close
              </button>
              <a
                href={`mailto:${selectedSubmission.email}?subject=Re: ${
                  selectedSubmission.subject || "Your inquiry"
                }`}
                className="btn btn-primary"
              >
                <i className="fas fa-reply me-2"></i>
                Reply via Email
              </a>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ContactFormData;
